/**
 * Panoramica Economica - VERSIONE FUSA
 * Combina: KPI mensili originali + Sezione YTD Legacy + Registro Operazioni
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  TrendingUp, TrendingDown, Euro, ArrowUpCircle, ArrowDownCircle,
  Percent, Calendar, ChevronLeft, ChevronRight, Receipt,
  Building, CreditCard, Wallet, PiggyBank, AlertCircle,
  Filter, Settings, Plus, Edit, Trash2, Droplets, Zap, 
  ShoppingCart, Wrench, Home, Sparkles, MoreHorizontal
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = [
  { value: 1, label: 'Gennaio', short: 'Gen' },
  { value: 2, label: 'Febbraio', short: 'Feb' },
  { value: 3, label: 'Marzo', short: 'Mar' },
  { value: 4, label: 'Aprile', short: 'Apr' },
  { value: 5, label: 'Maggio', short: 'Mag' },
  { value: 6, label: 'Giugno', short: 'Giu' },
  { value: 7, label: 'Luglio', short: 'Lug' },
  { value: 8, label: 'Agosto', short: 'Ago' },
  { value: 9, label: 'Settembre', short: 'Set' },
  { value: 10, label: 'Ottobre', short: 'Ott' },
  { value: 11, label: 'Novembre', short: 'Nov' },
  { value: 12, label: 'Dicembre', short: 'Dic' }
];

const EXPENSE_CATEGORIES = [
  { value: 'pulizie', label: 'Pulizie', icon: Sparkles, color: 'bg-blue-100 text-blue-600' },
  { value: 'lavanderia', label: 'Lavanderia', icon: Droplets, color: 'bg-cyan-100 text-cyan-600' },
  { value: 'manutenzione', label: 'Manutenzione', icon: Wrench, color: 'bg-orange-100 text-orange-600' },
  { value: 'utenze', label: 'Utenze', icon: Zap, color: 'bg-yellow-100 text-yellow-600' },
  { value: 'luce', label: 'Luce', icon: Zap, color: 'bg-amber-100 text-amber-600' },
  { value: 'fitto', label: 'Fitto/Affitto', icon: Home, color: 'bg-purple-100 text-purple-600' },
  { value: 'forniture', label: 'Forniture', icon: ShoppingCart, color: 'bg-green-100 text-green-600' },
  { value: 'altro', label: 'Altro', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-600' }
];

export default function EconomicOverview() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [filterType, setFilterType] = useState('all');
  
  // Dialog state per spese
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    descrizione: '',
    categoria: 'pulizie',
    importo: '',
    data: new Date().toISOString().split('T')[0]
  });

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];

  // Fetch dati originali (mensili)
  const fetchOverview = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/economic/overview/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(response.data);
    } catch (error) {
      console.error('Error fetching overview:', error);
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth]);

  // Fetch dati Legacy (YTD + mese corrente)
  const fetchDashboard = useCallback(async () => {
    if (!token) return;
    try {
      const [dashboardRes, expensesRes] = await Promise.all([
        axios.get(`${API}/admin/financial/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: selectedYear, month: selectedMonth }
        }),
        axios.get(`${API}/admin/financial/expenses`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { year: selectedYear, month: selectedMonth }
        })
      ]);
      setDashboard(dashboardRes.data);
      setExpenses(expensesRes.data?.expenses || []);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [token, selectedYear, selectedMonth]);

  useEffect(() => {
    if (token) {
      fetchOverview();
      fetchDashboard();
    }
  }, [token, fetchOverview, fetchDashboard]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const filteredOperations = data?.operazioni?.filter(op => {
    if (filterType === 'all') return true;
    return op.tipo === filterType;
  }) || [];

  // Funzioni spese
  const resetForm = () => {
    setExpenseForm({
      descrizione: '',
      categoria: 'pulizie',
      importo: '',
      data: new Date().toISOString().split('T')[0]
    });
    setEditingExpense(null);
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.descrizione || !expenseForm.importo) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    try {
      if (editingExpense) {
        await axios.put(`${API}/admin/financial/expenses/${editingExpense.id}`, expenseForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Spesa aggiornata');
      } else {
        await axios.post(`${API}/admin/financial/expenses`, expenseForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Spesa aggiunta');
      }
      setDialogOpen(false);
      resetForm();
      fetchDashboard();
      fetchOverview();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    try {
      await axios.delete(`${API}/admin/financial/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Spesa eliminata');
      fetchDashboard();
      fetchOverview();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      descrizione: expense.descrizione,
      categoria: expense.categoria,
      importo: expense.importo,
      data: expense.data
    });
    setDialogOpen(true);
  };

  const getCategoryInfo = (cat) => {
    return EXPENSE_CATEGORIES.find(c => c.value === cat) || EXPENSE_CATEGORIES[7];
  };

  const renderVariationBadge = (value, inverted = false) => {
    if (value === null || value === undefined) return null;
    const isPositive = inverted ? value < 0 : value > 0;
    return (
      <span className={`flex items-center text-xs font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
        {value > 0 ? '+' : ''}{value}%
      </span>
    );
  };

  const ytd = dashboard?.ytd || {};
  const meseCorrente = dashboard?.mese_corrente || {};
  const trend = dashboard?.trend_vs_anno_precedente;

  return (
    <AdminLayout title="Gestione Economica">
      <div className="space-y-6">
        {/* Header con navigazione mese e azioni */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-cinzel text-2xl text-[#1A202C]">Panoramica Economica</h1>
            <p className="text-sm text-gray-500">Panoramica finanziaria {selectedYear}</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Month Navigator */}
            <div className="flex items-center gap-1 bg-white rounded-lg border px-2 py-1">
              <Button variant="ghost" size="sm" onClick={prevMonth} className="h-7 w-7 p-0">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1 min-w-[120px] justify-center text-sm">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border-0 bg-transparent font-medium appearance-none cursor-pointer"
                >
                  {MONTHS.map(m => (
                    <option key={m.value} value={m.value}>{m.short}</option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="border-0 bg-transparent text-gray-500 appearance-none cursor-pointer"
                >
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <Button variant="ghost" size="sm" onClick={nextMonth} className="h-7 w-7 p-0">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            <Link to="/admin/economic/impostazioni">
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-1" />
                Impostazioni
              </Button>
            </Link>
            
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#C5A059] hover:bg-[#B08D4A] text-white">
                  <Plus className="w-4 h-4 mr-1" />
                  Spesa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingExpense ? 'Modifica Spesa' : 'Nuova Spesa'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <Label className="text-amber-800 font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Data Spesa *
                    </Label>
                    <Input
                      type="date"
                      value={expenseForm.data}
                      onChange={(e) => setExpenseForm({...expenseForm, data: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Descrizione *</Label>
                    <Input
                      value={expenseForm.descrizione}
                      onChange={(e) => setExpenseForm({...expenseForm, descrizione: e.target.value})}
                      placeholder="Es: Lavanderia biancheria"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Categoria</Label>
                      <select
                        value={expenseForm.categoria}
                        onChange={(e) => setExpenseForm({...expenseForm, categoria: e.target.value})}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                      >
                        {EXPENSE_CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Importo (€) *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={expenseForm.importo}
                        onChange={(e) => setExpenseForm({...expenseForm, importo: parseFloat(e.target.value) || ''})}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                      Annulla
                    </Button>
                    <Button onClick={handleSaveExpense} className="bg-[#C5A059] hover:bg-[#B08D4A]">
                      {editingExpense ? 'Salva' : 'Aggiungi'}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C5A059]"></div>
          </div>
        ) : (
          <>
            {/* ============== SEZIONE YTD (da Legacy) ============== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[10px] text-green-600 font-medium uppercase">Entrate YTD</p>
                      <p className="text-xl font-bold text-green-700">{formatCurrency(ytd.totale_lordo)}</p>
                    </div>
                    <TrendingUp className="w-5 h-5 text-green-300" />
                  </div>
                  {trend && (
                    <div className={`flex items-center mt-1 text-[10px] ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {trend >= 0 ? '+' : ''}{trend}% vs anno scorso
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[10px] text-blue-600 font-medium uppercase">Netto YTD</p>
                      <p className="text-xl font-bold text-blue-700">{formatCurrency(ytd.totale_netto)}</p>
                    </div>
                    <Euro className="w-5 h-5 text-blue-300" />
                  </div>
                  <p className="text-[9px] text-blue-500 mt-1">Dopo commissioni</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
                <CardContent className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[10px] text-red-600 font-medium uppercase">Spese YTD</p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(ytd.totale_spese)}</p>
                    </div>
                    <Receipt className="w-5 h-5 text-red-300" />
                  </div>
                </CardContent>
              </Card>

              <Card className={`bg-gradient-to-br ${(ytd.profitto_netto || 0) >= 0 ? 'from-amber-50 to-yellow-50 border-amber-200' : 'from-red-50 to-rose-50 border-red-200'}`}>
                <CardContent className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-[10px] text-[#C5A059] font-medium uppercase">Profitto YTD</p>
                      <p className={`text-xl font-bold ${(ytd.profitto_netto || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(ytd.profitto_netto)}
                      </p>
                    </div>
                    <Building className="w-5 h-5 text-[#C5A059]/50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ============== MESE CORRENTE + SPESE (da Legacy) ============== */}
            <div className="grid lg:grid-cols-2 gap-4">
              {/* Mese Corrente */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#C5A059]" />
                    Mese: {meseCorrente.mese || MONTHS.find(m => m.value === selectedMonth)?.label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between py-1.5 border-b">
                      <span className="text-gray-600">Lordo</span>
                      <span className="font-semibold">{formatCurrency(meseCorrente.totale_lordo)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b">
                      <span className="text-gray-600">- Commissioni OTA</span>
                      <span className="text-red-600">-{formatCurrency(meseCorrente.totale_commissioni)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b">
                      <span className="text-gray-600">- Cedolare Secca</span>
                      <span className="text-orange-600">-{formatCurrency(meseCorrente.cedolare_secca)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b">
                      <span className="text-gray-600">= Netto</span>
                      <span className="font-bold text-blue-600">{formatCurrency(meseCorrente.totale_netto)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b">
                      <span className="text-gray-600">- Spese Operative</span>
                      <span className="text-red-600">-{formatCurrency(meseCorrente.totale_spese)}</span>
                    </div>
                    <div className="flex justify-between py-2 bg-[#C5A059]/10 px-2 rounded">
                      <span className="font-medium">= PROFITTO</span>
                      <span className={`font-bold text-lg ${(meseCorrente.profitto_netto || meseCorrente.profitto || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(meseCorrente.profitto_netto || meseCorrente.profitto)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 pt-2 text-center text-xs">
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-500">Prenotazioni</p>
                        <p className="font-bold text-lg">{meseCorrente.num_prenotazioni || 0}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-500">Media/notte</p>
                        <p className="font-bold text-lg">{formatCurrency(meseCorrente.prezzo_medio_notte)}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Spese Mese Corrente */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-[#C5A059]" />
                      Spese Mese
                    </span>
                    <span className="text-xs font-normal text-gray-500">
                      Tot: {formatCurrency(expenses.reduce((s, e) => s + e.importo, 0))}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {expenses.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                      <Receipt className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Nessuna spesa questo mese</p>
                      <Button variant="link" onClick={() => setDialogOpen(true)} className="text-[#C5A059] text-sm">
                        Aggiungi spesa
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                      {expenses.map((expense) => {
                        const catInfo = getCategoryInfo(expense.categoria);
                        const IconComponent = catInfo.icon;
                        return (
                          <div key={expense.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded group">
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded-full ${catInfo.color}`}>
                                <IconComponent className="w-3 h-3" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">{expense.descrizione}</p>
                                <p className="text-[10px] text-gray-500">{expense.data}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-red-600 text-sm">-{formatCurrency(expense.importo)}</span>
                              <div className="opacity-0 group-hover:opacity-100 flex gap-0.5">
                                <Button variant="ghost" size="sm" onClick={() => openEditDialog(expense)} className="h-6 w-6 p-0">
                                  <Edit className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(expense.id)} className="h-6 w-6 p-0 text-red-500">
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Spese per Categoria YTD */}
            {dashboard?.spese_per_categoria && Object.keys(dashboard.spese_per_categoria).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Ripartizione Spese per Categoria (YTD)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(dashboard.spese_per_categoria).map(([cat, amount]) => {
                      const catInfo = getCategoryInfo(cat);
                      const IconComponent = catInfo.icon;
                      return (
                        <div key={cat} className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`p-1 rounded ${catInfo.color}`}>
                              <IconComponent className="w-3 h-3" />
                            </div>
                            <span className="text-xs font-medium capitalize">{catInfo.label}</span>
                          </div>
                          <p className="text-lg font-bold">{formatCurrency(amount)}</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ============== KPI MENSILI ORIGINALI ============== */}
            {data && (
              <>
                <div className="border-t pt-6">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Dettaglio Mese Selezionato</h3>
                </div>
                
                {/* KPI Cards - Row 1 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                  <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Entrate Lorde</p>
                          <p className="text-lg md:text-2xl font-bold text-blue-600 mt-0.5">{formatCurrency(data.entrate_lorde)}</p>
                        </div>
                        <Euro className="w-5 h-5 md:w-8 md:h-8 text-blue-200 flex-shrink-0" />
                      </div>
                      {renderVariationBadge(data.var_entrate_pct)}
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Trattenute</p>
                          <p className="text-lg md:text-2xl font-bold text-orange-600 mt-0.5">-{formatCurrency(data.totale_trattenute || data.cedolare_secca)}</p>
                        </div>
                        <Percent className="w-5 h-5 md:w-8 md:h-8 text-orange-200 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Entrate Nette</p>
                          <p className="text-lg md:text-2xl font-bold text-emerald-600 mt-0.5">{formatCurrency(data.entrate_nette || (data.entrate_lorde - data.cedolare_secca))}</p>
                        </div>
                        <ArrowUpCircle className="w-5 h-5 md:w-8 md:h-8 text-emerald-200 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Uscite</p>
                          <p className="text-lg md:text-2xl font-bold text-red-600 mt-0.5">-{formatCurrency(data.uscite)}</p>
                        </div>
                        <ArrowDownCircle className="w-5 h-5 md:w-8 md:h-8 text-red-200 flex-shrink-0" />
                      </div>
                      {renderVariationBadge(data.var_uscite_pct, true)}
                    </CardContent>
                  </Card>
                </div>

                {/* KPI Cards - Row 2 */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                  <Card className={`border-l-4 ${data.utile_netto >= 0 ? 'border-l-green-500' : 'border-l-red-500'} hover:shadow-md transition-shadow bg-gradient-to-r from-white to-gray-50`}>
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Utile Netto</p>
                          <p className={`text-lg md:text-2xl font-bold mt-0.5 ${data.utile_netto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(data.utile_netto)}
                          </p>
                        </div>
                        <PiggyBank className={`w-5 h-5 md:w-8 md:h-8 flex-shrink-0 ${data.utile_netto >= 0 ? 'text-green-200' : 'text-red-200'}`} />
                      </div>
                      {renderVariationBadge(data.var_utile_pct)}
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Da Incassare</p>
                          <p className="text-lg md:text-2xl font-bold text-amber-600 mt-0.5">{formatCurrency(data.da_incassare)}</p>
                        </div>
                        <Wallet className="w-5 h-5 md:w-8 md:h-8 text-amber-200 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Da Pagare</p>
                          <p className="text-lg md:text-2xl font-bold text-purple-600 mt-0.5">{formatCurrency(data.da_pagare)}</p>
                        </div>
                        <CreditCard className="w-5 h-5 md:w-8 md:h-8 text-purple-200 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-l-4 border-l-cyan-500 hover:shadow-md transition-shadow">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 uppercase font-medium truncate">Cash Flow</p>
                          <p className={`text-lg md:text-2xl font-bold mt-0.5 ${data.cash_flow >= 0 ? 'text-cyan-600' : 'text-red-600'}`}>
                            {formatCurrency(data.cash_flow)}
                          </p>
                        </div>
                        <TrendingUp className={`w-5 h-5 md:w-8 md:h-8 flex-shrink-0 ${data.cash_flow >= 0 ? 'text-cyan-200' : 'text-red-200'}`} />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Entrate per Canale + Uscite per Categoria */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                        <ArrowUpCircle className="w-4 h-4 text-green-500" />
                        Entrate per Canale
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(data.entrate_per_canale || {}).length === 0 ? (
                          <p className="text-sm text-gray-400 italic">Nessuna entrata nel mese</p>
                        ) : (
                          Object.entries(data.entrate_per_canale).map(([canale, importo]) => (
                            <div key={canale} className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
                              <span className="text-sm text-gray-600">{canale}</span>
                              <span className="font-medium text-green-600">{formatCurrency(importo)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                        <ArrowDownCircle className="w-4 h-4 text-red-500" />
                        Uscite per Categoria
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(data.uscite_per_categoria || {}).length === 0 ? (
                          <p className="text-sm text-gray-400 italic">Nessuna uscita nel mese</p>
                        ) : (
                          Object.entries(data.uscite_per_categoria).map(([cat, importo]) => (
                            <div key={cat} className="flex justify-between items-center py-1 border-b border-gray-100 last:border-0">
                              <span className="text-sm text-gray-600 capitalize">{cat}</span>
                              <span className="font-medium text-red-600">-{formatCurrency(importo)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Registro Operazioni */}
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-[#C5A059]" />
                        Registro Operazioni
                        <span className="text-sm font-normal text-gray-500">
                          ({data.totale_operazioni || filteredOperations.length} movimenti)
                        </span>
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <select
                          value={filterType}
                          onChange={(e) => setFilterType(e.target.value)}
                          className="border rounded-md px-2 py-1 text-sm"
                        >
                          <option value="all">Tutti</option>
                          <option value="entrata">Solo Entrate</option>
                          <option value="uscita">Solo Uscite</option>
                        </select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {filteredOperations.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Receipt className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>Nessuna operazione nel mese selezionato</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop Table */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b text-left text-gray-500">
                                <th className="pb-2 font-medium">Data</th>
                                <th className="pb-2 font-medium">Tipo</th>
                                <th className="pb-2 font-medium">Descrizione</th>
                                <th className="pb-2 font-medium">Canale</th>
                                <th className="pb-2 font-medium text-right">Importo</th>
                                <th className="pb-2 font-medium">Stato</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredOperations.slice(0, 20).map((op, idx) => (
                                <tr key={op.id || idx} className="border-b border-gray-50 hover:bg-gray-50">
                                  <td className="py-3 text-gray-600">{formatDate(op.data)}</td>
                                  <td className="py-3">
                                    {op.tipo === 'entrata' ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                        <ArrowUpCircle className="w-3 h-3 mr-1" />
                                        Entrata
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                        <ArrowDownCircle className="w-3 h-3 mr-1" />
                                        Uscita
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 max-w-[200px] truncate" title={op.descrizione}>
                                    {op.descrizione}
                                  </td>
                                  <td className="py-3 text-gray-500">
                                    {op.canale || op.fornitore || '-'}
                                  </td>
                                  <td className={`py-3 text-right font-medium ${op.tipo === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                                    {op.tipo === 'entrata' ? '+' : '-'}{formatCurrency(op.importo_netto || op.importo_lordo)}
                                  </td>
                                  <td className="py-3">
                                    {op.stato === 'incassata' || op.stato === 'pagata' ? (
                                      <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">OK</span>
                                    ) : (
                                      <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">Attesa</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="md:hidden space-y-2">
                          {filteredOperations.slice(0, 15).map((op, idx) => (
                            <div 
                              key={op.id || idx} 
                              className={`p-3 rounded-lg border-l-4 bg-gray-50 ${
                                op.tipo === 'entrata' ? 'border-l-emerald-500' : 'border-l-red-500'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-1">
                                <div className="flex-1">
                                  <div className="text-xs text-gray-500">{formatDate(op.data)}</div>
                                  <div className="font-medium text-gray-800 text-sm line-clamp-2">{op.descrizione}</div>
                                </div>
                                <div className={`font-bold text-sm ${op.tipo === 'entrata' ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {op.tipo === 'entrata' ? '+' : '-'}{formatCurrency(op.importo_netto || op.importo_lordo)}
                                </div>
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500">{op.canale || op.fornitore || op.categoria}</span>
                                {op.stato === 'incassata' || op.stato === 'pagata' ? (
                                  <span className="px-2 py-0.5 rounded bg-green-100 text-green-700">OK</span>
                                ) : (
                                  <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700">Attesa</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {filteredOperations.length > 15 && (
                          <div className="text-center py-3 mt-2">
                            <Link to="/admin/economic/prima-nota" className="text-[#C5A059] hover:underline text-sm">
                              Vedi tutti i movimenti ({filteredOperations.length})
                            </Link>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <div className="flex flex-wrap gap-3">
                  <Link to="/admin/economic/entrate">
                    <Button variant="outline" className="border-green-300 text-green-600 hover:bg-green-50">
                      <ArrowUpCircle className="w-4 h-4 mr-2" />
                      Gestisci Entrate
                    </Button>
                  </Link>
                  <Link to="/admin/economic/spese">
                    <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                      <ArrowDownCircle className="w-4 h-4 mr-2" />
                      Gestisci Spese
                    </Button>
                  </Link>
                  <Link to="/admin/economic/prima-nota">
                    <Button variant="outline">
                      <Receipt className="w-4 h-4 mr-2" />
                      Prima Nota
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
